import { useState } from 'react';

import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { CalculatedDateNotice, useCalculatedDate } from './procedural-calculated-date';
import {
  flashEventRow,
  formatDate,
  formatDateRange,
  hasUpdatedField,
  mapDeltaTypeToCounting,
} from './procedural-helpers';
import {
  CONFIDENCE_LABELS,
  COUNTING_LABEL,
  DateTbdLabel,
  DetailCell,
  DetailsAccordion,
  SourceFileButton,
  UnconfirmedChip,
  formatRelationship,
} from './procedural-row-parts';
import { ProceduralSourceRef } from './procedural-source-drawer';
import { Permission } from '../../../../../../../../backend/src/auth/permissions/permission';
import {
  DeltaIntervalType,
  EventConfidence,
} from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-enums';
import type {
  ProceduralTimelineEventSuggestion,
  SuggestionKind,
} from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-event-suggestion.entity';
import type { ProceduralTimelineEvent } from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-event.entity';
import {
  useAcceptProceduralSuggestion,
  useRejectProceduralSuggestion,
} from '@/api/mutations/useProceduralSuggestionAction';
import { useSetProceduralSuggestionDate } from '@/api/mutations/useSetProceduralSuggestionDate';
import { PermissionGate } from '@/hooks/useHasPermission';
import { Chip } from '@/new/components/chip';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { combineDateAndTime, instantToCivilUtc } from '@/new/components/timeline-calculator/civil-date';
import { EmbeddedCalculator } from '@/new/components/timeline-calculator/embedded-calculator';
import { formatTimeZoneLabel } from '@/new/lib/time-zones';
import { cn } from '@/new/lib/utils';

interface ProceduralSuggestionRowProps {
  suggestion: ProceduralTimelineEventSuggestion;
  eventsById: Record<string, ProceduralTimelineEvent>;
  /** How many other pending suggestions depend upon this one. */
  dependentSuggestionCount: number;
  onOpenSource: (source: ProceduralSourceRef) => void;
}

/** Title and lead copy for the reject confirmation, by suggestion kind. */
const REJECT_COPY: Record<SuggestionKind, { title: string; lead: string }> = {
  add: { title: 'Reject suggested event?', lead: "The suggested event won't be added to the timeline." },
  update: { title: 'Reject suggested update?', lead: "The suggested update won't be applied to the event." },
  delete: { title: 'Reject suggested deletion?', lead: 'The event will be kept and this suggestion dismissed.' },
};

interface RibbonStyle {
  card: string;
  ribbon: string;
  icon: string;
  title: string;
}

const RIBBON_STYLES: Partial<Record<SuggestionKind, RibbonStyle>> = {
  add: {
    card: 'border-sky-200 bg-white',
    ribbon: 'bg-sky-100/70 text-sky-700',
    icon: 'lucide--plus',
    title: 'Suggested event',
  },
  update: {
    card: 'border-violet-200 bg-white',
    ribbon: 'bg-violet-100/70 text-violet-700',
    icon: 'lucide--refresh-cw',
    title: 'Updated event',
  },
};

interface EffectiveView {
  name: string;
  description: string | null;
  startDateTime: Date | string | null;
  endDateTime: Date | string | null;
  timeZone: string | null;
}

function effectiveView(suggestion: ProceduralTimelineEventSuggestion): EffectiveView {
  const existing = suggestion.existingEvent;
  if (suggestion.kind === 'add') {
    return {
      name: suggestion.proposedName ?? '(unnamed)',
      description: suggestion.proposedDescription,
      startDateTime: suggestion.proposedStartDateTime,
      endDateTime: suggestion.proposedEndDateTime,
      timeZone: suggestion.proposedTimeZone,
    };
  }
  // Update / Delete render off the existing event. An Update overlays exactly
  // its `updatedFields` — a listed field's proposed value applies even when
  // null (an intentional clear). Name, description, and time zone are never
  // changed by an update.
  return {
    name: existing?.name ?? '(unknown event)',
    description: existing?.description ?? null,
    startDateTime: hasUpdatedField(suggestion, 'startDateTime')
      ? suggestion.proposedStartDateTime
      : (existing?.startDateTime ?? null),
    endDateTime: hasUpdatedField(suggestion, 'endDateTime')
      ? suggestion.proposedEndDateTime
      : (existing?.endDateTime ?? null),
    timeZone: existing?.timeZone ?? null,
  };
}

interface ChangedFields {
  startDateTime: boolean;
  endDateTime: boolean;
  /** Any of interval, interval type, or anchor target changed. */
  anchor: boolean;
  isConfirmed: boolean;
}

const CHANGED_LABELS: Record<keyof ChangedFields, string> = {
  startDateTime: 'Start date',
  endDateTime: 'End date',
  anchor: 'Dependent date',
  isConfirmed: 'Confirmation',
};

/** Render changed-field labels as "X changed", "X and Y changed", or "X, Y, and Z changed". */
function formatChangedFields(labels: string[]): string {
  if (labels.length === 1) return `${labels[0]} changed`;
  if (labels.length === 2) return `${labels[0]} and ${labels[1]} changed`;
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]} changed`;
}

function detectChanges(s: ProceduralTimelineEventSuggestion): ChangedFields | null {
  // Backend contract: for kind=update, `updatedFields` is the authoritative
  // list of what changed. Proposed values can't be null-sniffed — a listed
  // field's proposed value may be null on purpose (an intentional clear).
  if (s.kind !== 'update') return null;
  return {
    startDateTime: hasUpdatedField(s, 'startDateTime'),
    endDateTime: hasUpdatedField(s, 'endDateTime'),
    anchor:
      hasUpdatedField(s, 'anchorEventId') ||
      hasUpdatedField(s, 'deltaInterval') ||
      hasUpdatedField(s, 'deltaIntervalType'),
    isConfirmed: hasUpdatedField(s, 'isConfirmed'),
  };
}

/** Mirror the post-accept confirmation state for the Unconfirmed chip. */
function isEffectivelyUnconfirmed(s: ProceduralTimelineEventSuggestion): boolean {
  if (s.kind === 'add') return s.proposedIsConfirmed !== true;
  if (s.kind === 'update') {
    return hasUpdatedField(s, 'isConfirmed') ? s.proposedIsConfirmed !== true : s.existingEvent?.isConfirmed === false;
  }
  // Delete removes the event, so the chip is meaningless.
  return false;
}

interface EffectiveAnchor {
  anchorEventId: string | null;
  interval: ProceduralTimelineEvent['deltaInterval'];
  intervalType: ProceduralTimelineEventSuggestion['proposedDeltaIntervalType'];
}

/**
 * Post-accept anchor relationship, overlaid like the dates: an Update applies a
 * field only when `updatedFields` lists it, otherwise the existing event's
 * value shows through. Without this, a partial anchor change (only the target,
 * or only the interval) drops its unchanged half, and an update that leaves the
 * anchor alone loses the relationship entirely.
 */
function effectiveAnchor(s: ProceduralTimelineEventSuggestion): EffectiveAnchor {
  const existing = s.existingEvent;
  if (s.kind === 'add') {
    return {
      anchorEventId: s.proposedAnchorExistingEventId,
      interval: s.proposedDeltaInterval,
      intervalType: s.proposedDeltaIntervalType,
    };
  }
  if (s.kind === 'delete') {
    return {
      anchorEventId: existing?.anchorEventId ?? null,
      interval: existing?.deltaInterval ?? null,
      intervalType: existing?.deltaIntervalType ?? null,
    };
  }
  return {
    anchorEventId: hasUpdatedField(s, 'anchorEventId')
      ? s.proposedAnchorExistingEventId
      : (existing?.anchorEventId ?? null),
    interval: hasUpdatedField(s, 'deltaInterval') ? s.proposedDeltaInterval : (existing?.deltaInterval ?? null),
    intervalType: hasUpdatedField(s, 'deltaIntervalType')
      ? s.proposedDeltaIntervalType
      : (existing?.deltaIntervalType ?? null),
  };
}

export function ProceduralSuggestionRow({
  suggestion,
  eventsById,
  dependentSuggestionCount,
  onOpenSource,
}: ProceduralSuggestionRowProps) {
  const { matterId } = useParams();
  const [isRejectConfirmOpen, setIsRejectConfirmOpen] = useState(false);
  const { mutate: accept, isLoading: isAccepting } = useAcceptProceduralSuggestion();
  const { mutate: reject, isLoading: isRejecting } = useRejectProceduralSuggestion();

  const view = effectiveView(suggestion);
  const chunk = suggestion.chunk;
  const sourceFileName = chunk?.file?.name ?? null;
  const isDelete = suggestion.kind === 'delete';
  const isUpdate = suggestion.kind === 'update';
  const ribbon = RIBBON_STYLES[suggestion.kind] ?? null;
  const isUnconfirmed = isEffectivelyUnconfirmed(suggestion);
  const isPending = isAccepting || isRejecting;
  const isBlocked = suggestion.proposedAnchorSuggestionId !== null;
  const existing = suggestion.existingEvent;
  const changed = detectChanges(suggestion);
  const changedLabels = changed
    ? (Object.keys(changed) as Array<keyof ChangedFields>).filter((k) => changed[k]).map((k) => CHANGED_LABELS[k])
    : [];

  const anchor = effectiveAnchor(suggestion);
  const anchorEvent = anchor.anchorEventId !== null ? (eventsById[anchor.anchorEventId] ?? null) : null;
  const anchorEventName = anchorEvent?.name ?? null;
  const relationship = formatRelationship(anchor.interval, anchor.intervalType, anchorEventName);
  const dateRangeText = formatDateRange(view.startDateTime, view.endDateTime);

  const effectiveJurisdiction =
    suggestion.kind === 'add' ? suggestion.proposedJurisdiction : (existing?.jurisdiction ?? null);
  const calculatedDate = useCalculatedDate({
    matterId,
    anchorStartDateTime: anchorEvent?.startDateTime ?? null,
    days: anchor.interval?.days ?? null,
    deltaIntervalType: anchor.intervalType,
    jurisdiction: effectiveJurisdiction,
    currentStartDateTime: view.startDateTime,
  });

  // The calculator opens in a dialog (not the floating event calculator) so the
  // resolved date is saved onto the suggestion's proposed date, to be applied
  // when it's accepted. Only meaningful when anchored to a real event.
  const [isCalcOpen, setIsCalcOpen] = useState(false);
  const setSuggestionDate = useSetProceduralSuggestionDate();
  const openCalc = () => setIsCalcOpen(true);

  const handleApplyCalcDate = (date: Date) => {
    if (matterId === undefined) return;
    const civil = instantToCivilUtc(date) ?? date;
    setSuggestionDate.mutate(
      { matterId, suggestionId: suggestion.id, startDateTime: combineDateAndTime(civil, '') },
      {
        onSuccess: () => {
          setIsCalcOpen(false);
          toast.success('Date updated.');
        },
        onError: (error) => {
          toast.error(error.message || 'Failed to set the date');
        },
      },
    );
  };

  const oldAnchorEventName = existing?.anchorEventId ? (eventsById[existing.anchorEventId]?.name ?? null) : null;
  const oldRelationship = changed?.anchor
    ? formatRelationship(existing?.deltaInterval ?? null, existing?.deltaIntervalType ?? null, oldAnchorEventName)
    : null;
  const oldDateRangeText =
    changed?.startDateTime || changed?.endDateTime
      ? formatDateRange(existing?.startDateTime ?? null, existing?.endDateTime ?? null)
      : null;
  // An update that gives a previously-undated (TBD) event a date
  const showStruckTbd = changed?.startDateTime === true && oldDateRangeText === null && dateRangeText !== null;

  // Details-panel values, mirroring the event row's panel with the
  // suggestion's post-accept state (proposed value, falling back to existing).
  const detailConfidence = suggestion.proposedConfidence ?? existing?.confidence ?? null;
  const detailBasis = anchor.anchorEventId !== null ? anchor.intervalType : null;
  const oldBasis =
    isUpdate && hasUpdatedField(suggestion, 'deltaIntervalType') && existing?.anchorEventId
      ? existing.deltaIntervalType
      : null;

  const handleSourceClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!chunk?.file) return;
    onOpenSource({
      fileId: chunk.file.id,
      fileName: chunk.file.name,
      pageNumber: chunk.pageNumber,
      chunkId: chunk.id,
      content: chunk.content,
      boundingBox: chunk.boundingBox,
    });
  };

  const handleAccept = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!matterId) return;
    accept(
      { matterId, suggestionId: suggestion.id },
      {
        onSuccess: () => {
          if (suggestion.kind === 'update' && suggestion.existingEventId !== null) {
            flashEventRow(suggestion.existingEventId);
          }
        },
        onError: (error) => {
          toast.error(error.message || 'Failed to accept suggestion');
        },
      },
    );
  };

  const handleRejectClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsRejectConfirmOpen(true);
  };

  const handleConfirmReject = () => {
    if (!matterId) return;
    reject(
      { matterId, suggestionId: suggestion.id },
      {
        onSuccess: () => {
          setIsRejectConfirmOpen(false);
        },
        onError: (error) => {
          toast.error(error.message || 'Failed to reject suggestion');
        },
      },
    );
  };

  return (
    <>
      <div
        className={cn(
          'w-full overflow-hidden rounded-lg border',
          ribbon ? ribbon.card : 'border-dashed border-gray-300 bg-white',
          isBlocked && 'opacity-60',
        )}
      >
        {ribbon && (
          <div
            className={cn(
              'flex items-center gap-1.5 px-4 py-1.5 text-[11px] font-semibold tracking-wide uppercase',
              ribbon.ribbon,
            )}
          >
            <Icon className={cn('shrink-0', ribbon.icon)} />
            {ribbon.title}
            {changedLabels.length > 0 && (
              <span className="font-normal tracking-normal normal-case opacity-80">
                {'•'} {formatChangedFields(changedLabels)}
              </span>
            )}
            {detailConfidence !== null && detailConfidence !== EventConfidence.Manual && (
              <span className="font-normal tracking-normal normal-case opacity-80">
                {'•'} {CONFIDENCE_LABELS[detailConfidence]}
              </span>
            )}
          </div>
        )}
        <div className="flex w-full flex-col gap-2 py-3 pr-6 pl-4 text-left">
          <div className="flex items-center justify-between gap-4">
            <div className="flex min-w-0 items-center gap-2">
              <p className={cn('text-sm text-gray-500', isDelete && 'line-through')}>
                {oldDateRangeText && <span className="mr-2 text-gray-400 line-through">{oldDateRangeText}</span>}
                {showStruckTbd && <span className="mr-2 text-gray-400 line-through">TBD</span>}
                {dateRangeText ?? <DateTbdLabel />}
              </p>
              {!isDelete && <CalculatedDateNotice state={calculatedDate} onOpenCalculator={openCalc} />}
            </div>

            <div className="flex shrink-0 items-center gap-2">
              {isUnconfirmed && <UnconfirmedChip />}
              {sourceFileName && <SourceFileButton fileName={sourceFileName} onClick={handleSourceClick} />}
              {!isDelete && !isBlocked && anchorEvent !== null && (
                <PermissionGate permission={Permission.ProceduralTimelineEdit} uiMode="disabled">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="iconSm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsCalcOpen(true);
                        }}
                        aria-label="Open timeline calculator"
                      >
                        <Icon className="lucide--calculator" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Set the date with the calculator before accepting</TooltipContent>
                  </Tooltip>
                </PermissionGate>
              )}
              <PermissionGate permission={Permission.ProceduralTimelineEdit} uiMode="disabled">
                {isBlocked ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span tabIndex={0} className="inline-flex cursor-not-allowed items-center gap-2">
                        <Button variant="primary" size="sm" disabled>
                          <Icon className="lucide--check" />
                          Accept
                        </Button>
                        <Button variant="ghost" size="sm" disabled>
                          <Icon className="lucide--x" />
                          Reject
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>Verify the suggestion this event depends on first.</TooltipContent>
                  </Tooltip>
                ) : (
                  <>
                    <Button variant="primary" size="sm" onClick={handleAccept} disabled={isPending}>
                      <Icon className={isAccepting ? 'lucide--loader-2 animate-spin' : 'lucide--check'} />
                      Accept
                    </Button>
                    <Button variant="ghost" size="sm" onClick={handleRejectClick} disabled={isPending}>
                      <Icon className={isRejecting ? 'lucide--loader-2 animate-spin' : 'lucide--x'} />
                      Reject
                    </Button>
                  </>
                )}
              </PermissionGate>
            </div>
          </div>

          <p className={cn('-mt-1.5 min-w-0 truncate text-lg text-gray-900', isDelete && 'text-gray-500 line-through')}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>{view.name}</span>
              </TooltipTrigger>
              <TooltipContent>{view.name}</TooltipContent>
            </Tooltip>
          </p>

          {oldRelationship && (
            <p className="-mt-1 flex items-center gap-1.5 text-xs font-light text-gray-400 line-through">
              <Icon className="lucide--link shrink-0 text-sky-300" />
              {oldRelationship}
            </p>
          )}
          {relationship && (
            <p
              className={cn(
                'flex items-center gap-1.5 text-xs font-light text-gray-500',
                !oldRelationship && '-mt-1',
                isDelete && 'line-through',
              )}
            >
              <Icon className="lucide--link shrink-0 text-sky-600" />
              {relationship}
            </p>
          )}

          {isDelete && (
            <div className="flex flex-wrap items-center gap-1.5">
              <Chip className="rounded-lg border border-red-200 bg-red-50 text-red-700">
                <Icon className="lucide--minus-circle text-red-600" />
                Delete
              </Chip>
            </div>
          )}

          {view.description && (
            <p className={cn('text-sm text-gray-600', isDelete && 'line-through')}>{view.description}</p>
          )}

          <DetailsAccordion
            leading={
              suggestion.reasoning ? (
                <p className="flex items-start gap-1.5 text-xs text-gray-500 italic">
                  <Icon className="lucide--sparkles mt-0.5 shrink-0 text-gray-400" />
                  <span>{suggestion.reasoning}</span>
                </p>
              ) : undefined
            }
          >
            {detailConfidence !== null && (
              <DetailCell label="Confidence">{CONFIDENCE_LABELS[detailConfidence]}</DetailCell>
            )}
            <DetailCell label="Suggested">{formatDate(suggestion.createdAt)}</DetailCell>
            <DetailCell label="Time zone">{view.timeZone ? formatTimeZoneLabel(view.timeZone) : 'Not set'}</DetailCell>
            {detailBasis !== null && (
              <DetailCell label="Interval basis">
                {oldBasis !== null && oldBasis !== detailBasis && (
                  <span className="mr-1.5 text-gray-400 line-through">{COUNTING_LABEL[oldBasis]}</span>
                )}
                {COUNTING_LABEL[detailBasis]}
              </DetailCell>
            )}
          </DetailsAccordion>
        </div>
      </div>

      <ConfirmationModal
        isOpen={isRejectConfirmOpen}
        onOpenChange={setIsRejectConfirmOpen}
        title={REJECT_COPY[suggestion.kind].title}
        onConfirm={handleConfirmReject}
        isLoading={isRejecting}
      >
        <p className="text-sm text-gray-700">{REJECT_COPY[suggestion.kind].lead}</p>
        {dependentSuggestionCount > 0 && (
          <p className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <Icon className="lucide--triangle-alert mt-0.5 shrink-0 text-amber-600" />
            <span>
              {dependentSuggestionCount === 1
                ? 'Another suggested event depends upon this one. Rejecting it will remove that dependency, so it will no longer be calculated from this event.'
                : `${dependentSuggestionCount} other suggested events depend upon this one. Rejecting it will remove those dependencies, so they will no longer be calculated from this event.`}
            </span>
          </p>
        )}
      </ConfirmationModal>

      {matterId !== undefined && !isBlocked && anchorEvent !== null && (
        <Dialog open={isCalcOpen} onOpenChange={setIsCalcOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Timeline calculator</DialogTitle>
              <DialogDescription>Set the date for this suggested event before accepting.</DialogDescription>
            </DialogHeader>
            <EmbeddedCalculator
              matterId={matterId}
              sourceDate={anchorEvent.startDateTime !== null ? new Date(anchorEvent.startDateTime) : null}
              days={anchor.interval?.days ?? 0}
              counting={mapDeltaTypeToCounting(
                anchor.intervalType ?? DeltaIntervalType.Calendar,
                effectiveJurisdiction,
              )}
              onApply={handleApplyCalcDate}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
